import { observer } from 'mobx-react';
import { useMemo } from 'react';

import Link from '../../../../app/shared/components/Link';
import ThreeDotsLoader from '../../../../app/shared/components/Loaders/ThreeDotsLoader';
import type { RelativeUrl } from '../../../../app/shared/Url';
import type { LoadingState } from '../../../constants';
import type { ProductTableTranslation } from '../../../localization/ModuleTranslation';
import type { PricelistProductData, PricelistProductFamilyData } from '../../../model';
import Brand from '../../../shared/Brand/Brand';
import ProductImage from '../../../shared/ProductImage/ProductImage';
import {
  Cell,
  Collection,
  Column,
  DataGrid,
  DataGridBody,
  DataGridHeader,
  DataGridLoadMore,
  Row,
  type DataGridExpansion,
  type DataGridSelection,
} from '../../../ui/DataGrid/DataGrid';
import LoadingStatus from '../../../ui/LoadingStatus/LoadingStatus';
import Price from '../../../ui/Price/Price';
import { SidePanelTrigger } from '../../../ui/SidePanel/SidePanel';
import { PRODUCT_COLUMN_IDS } from '../constants';
import ExpansionState from '../ExpansionState';
import ProductSelectionState from '../ProductSelectionState';
import SortState from '../SortState';
import css from './ProductTable.css';
import ProductTableSkeleton from './ProductTableSkeleton/ProductTableSkeleton';

/**
 * Midway between a family row (68px) and a model row (80px), both measured in the browser: the
 * collapsed list is all families, an expanded one mostly models, and either is a common view.
 */
const ESTIMATED_ROW_HEIGHT = 74;
/** Measured in the browser: the load-more row. */
const LOADER_HEIGHT = 62;

interface ProductTableProps {
  currency: string;
  expansion: ExpansionState;
  families: PricelistProductFamilyData[];
  canLoadMoreProducts: boolean;
  loadingState: LoadingState;
  selection: ProductSelectionState;
  sort: SortState;
  translation: ProductTableTranslation;
  getDetailsRouteUrl: (product: PricelistProductData) => RelativeUrl;
  isItemActive: (id: string) => boolean;
  onDetailsToggle: (product: { familyId: string; modelId?: string }) => void;
  onLoadMore: () => void;
}

const ProductTable = observer(
  ({
    currency,
    expansion,
    families,
    canLoadMoreProducts,
    loadingState,
    selection,
    sort,
    translation,
    getDetailsRouteUrl,
    isItemActive,
    onDetailsToggle,
    onLoadMore,
  }: ProductTableProps) => {
    const gridExpansion = useMemo<DataGridExpansion>(
      () => ({
        columnLabel: translation.expandColumnLabel,
        expandLabel: translation.expandFamily,
        collapseLabel: translation.collapseFamily,
      }),
      [translation],
    );
    const gridSelection = useMemo<DataGridSelection>(
      () => ({
        selectAllLabel: translation.selectAll,
        rowLabel: translation.selectProduct,
        isAllIndeterminate: () => selection.isAllIndeterminate,
        isAllSelected: () => selection.isAllSelected,
        isRowIndeterminate: (id) => selection.isIndeterminate(id),
        isRowSelected: (id) => selection.isSelected(id),
        onToggleAll: selection.toggleAll,
        onToggleRow: selection.toggle,
      }),
      [selection, translation],
    );
    const isLoadingInitialData = loadingState === 'loading';
    const isLoading = isLoadingInitialData || loadingState === 'loadingMore';

    return (
      <>
        {isLoading && (
          <LoadingStatus message={isLoadingInitialData ? translation.loadingLabel : translation.loadingMoreLabel} />
        )}
        <DataGrid
          className={css.table}
          estimatedRowHeight={ESTIMATED_ROW_HEIGHT}
          expandedKeys={expansion.expandedIds}
          expansion={gridExpansion}
          loaderHeight={LOADER_HEIGHT}
          selection={gridSelection}
          sortDescriptor={sort.descriptor}
          aria-label={translation.title}
          onExpandedChange={(keys) => expansion.setExpanded([...keys].map((key) => String(key)))}
          onSortChange={(descriptor) => sort.setSort(String(descriptor.column), descriptor.direction)}
        >
          <DataGridHeader>
            <Column id={PRODUCT_COLUMN_IDS.ItemNo} minWidth={90}>
              {translation.itemNo}
            </Column>
            <Column id={PRODUCT_COLUMN_IDS.Image}>{translation.image}</Column>
            <Column id={PRODUCT_COLUMN_IDS.Brand} allowsSorting>
              {translation.brand}
            </Column>
            <Column id={PRODUCT_COLUMN_IDS.Model} minWidth={200} width="3fr" allowsSorting isRowHeader>
              {translation.model}
            </Column>
            <Column
              id={PRODUCT_COLUMN_IDS.Price}
              minWidth={120}
              width="2fr"
              tooltip={translation.priceInfo}
              allowsSorting
            >
              {translation.price} <span className={css.currency}>({currency})</span>
            </Column>
            <Column id={PRODUCT_COLUMN_IDS.Actions} minWidth={96}>
              <span className="visuallyHidden">{translation.actionLabel}</span>
            </Column>
          </DataGridHeader>
          <DataGridBody renderEmptyState={() => <div className={css.emptyState}>{translation.emptyText}</div>}>
            {isLoadingInitialData ? (
              <ProductTableSkeleton />
            ) : (
              <Collection items={families}>
                {(family) => (
                  <Row id={family.id} className={css.familyRow} onAction={() => expansion.toggle(family.id)}>
                    <Cell />
                    <Cell>
                      <ProductImage
                        imageUrl={family.image?.url}
                        productName={family.name}
                        productDescription={`${family.brandName}, ${family.products.length} ${translation.productsCountLabel}`}
                        translation={translation.productImage}
                        size="small"
                      />
                    </Cell>
                    <Cell>
                      <Brand brandId={family.brandId} brandName={family.brandName} />
                    </Cell>
                    <Cell>
                      <span className={css.familyNameCellInner}>
                        <span className={css.name}>{family.name}</span>
                        {/* TODO: Uncomment if the property is available in API response */}
                        {/* {family.isNew && <Badge className={css.badge}>{translation.newBadge}</Badge>} */}
                        <span
                          className={css.modelsCount}
                        >{`${family.products.length} ${translation.productsCountLabel}`}</span>
                      </span>
                    </Cell>
                    <Cell>
                      <span className={css.priceFromLabel}>{translation.fromLabel}</span>
                      <Price className={css.price} amount={family.startingPrice.amount} />
                    </Cell>
                    <Cell>
                      <DetailsButton
                        isPressed={() => isItemActive(family.id)}
                        onClick={() => onDetailsToggle({ familyId: family.id })}
                      >
                        {translation.familyActionLabel}
                      </DetailsButton>
                    </Cell>
                    <Collection items={family.products}>
                      {(product) => (
                        <Row id={product.id} className={css.modelRow}>
                          <Cell>{product.code}</Cell>
                          <Cell>
                            <ProductImage
                              imageUrl={product.image?.url}
                              productName={product.name}
                              productDescription={product.shortDescription}
                              translation={translation.productImage}
                              size="small"
                            />
                          </Cell>
                          <Cell />
                          <Cell>
                            <div className={css.modelCellInner}>
                              <Link className={css.link} to={getDetailsRouteUrl(product)}>
                                {product.name}
                              </Link>
                              <span className={css.modelDescription}>{product.shortDescription}</span>
                              {product.types.any() && (
                                <ul className={css.modelTypes}>
                                  {product.types.map((i) => (
                                    <li key={i}>{i}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </Cell>
                          <Cell>
                            <span className={css.priceFromLabel}>{translation.fromLabel}</span>{' '}
                            <Price className={css.price} amount={product.startingPrice.amount} />
                          </Cell>
                          <Cell>
                            <DetailsButton
                              isPressed={() => isItemActive(product.id)}
                              onClick={() => onDetailsToggle({ familyId: family.id, modelId: product.id })}
                            >
                              {translation.modelActionLabel}
                            </DetailsButton>
                          </Cell>
                        </Row>
                      )}
                    </Collection>
                  </Row>
                )}
              </Collection>
            )}
            {canLoadMoreProducts && (
              <DataGridLoadMore isLoading={loadingState === 'loadingMore'} scrollOffset={0.5} onLoadMore={onLoadMore}>
                <div className={css.loadingMore}>
                  <ThreeDotsLoader />
                </div>
              </DataGridLoadMore>
            )}
          </DataGridBody>
        </DataGrid>
      </>
    );
  },
);

export default ProductTable;

interface DetailsButtonProps {
  children: React.ReactNode;
  isPressed: () => boolean;
  onClick: () => void;
}

const DetailsButton = observer(({ children, isPressed, onClick }: DetailsButtonProps) => (
  <SidePanelTrigger className={css.detailsButton} isPressed={isPressed()} onClick={onClick}>
    {children}
  </SidePanelTrigger>
));
